'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { planAutoBets, type AutoBetPlan, type AutoBetRule, type AutoBetSkip } from './birdieAutoBet'
import {
  IDLE_AUTO_BET,
  applyAutoBetPlan,
  autoBetStep,
  clearsBoard,
  type AutoBetEvent,
  type AutoBetMachineState,
  type AutoBetStopReason
} from './birdieAutoBetMachine'
import type { BirdieChipAction, ChipLimits, ChipOutcome } from './birdieChips'
import type { BirdieTileView } from './tileViewModel'

export interface UseBirdieAutoBet {
  state: AutoBetMachineState
  armed: boolean
  arm: (rule: AutoBetRule) => void
  disarm: () => void
  /** A hand on the board: turns the rule off, leaves the chips. */
  noteManualEdit: () => void
  /** The close-time submission's verdict for a round — spends the count only when bets went out. */
  noteWireResult: (raceId: string | undefined, betsActuallySent: boolean) => void
  /** What `rule` would put on the open round; null while nothing can be placed. */
  previewFor: (rule: AutoBetRule) => AutoBetPlan | null
}

type AutoBetLogType = 'started' | 'stopped' | 'bet_placed' | 'skipped' | 'capped'

/**
 * Thin React skin over `autoBetStep` (B32). The machine decides; this hook
 * feeds it the board's own signals and spends its answers: a plan goes onto
 * the board through the ordinary `place` actions, a skip and a stop go to the
 * board's notice, and every one of them is logged under the platform's own
 * `auto_bet` action, plus the game and the zone.
 *
 * The state is held twice, as the board holds its chips: in `useState` for
 * the render and in a ref for the step that runs between renders, so two
 * steps in one tick — the plan and its placement report — both see the
 * first one's result.
 *
 * The round is planned from an effect rather than from a socket callback so
 * it runs AFTER the board's own `race_ready` reset in the same commit: the
 * host calls this hook below that effect, and effects run in declaration
 * order. A plan placed before the reset would be wiped by it.
 */
export function useBirdieAutoBet(input: {
  raceId: string | undefined
  tiles: readonly BirdieTileView[]
  limits: ChipLimits
  marketIdByOption: ReadonlyMap<string, string>
  /** `limits.roundPriced && !tilesLocked && !betsSubmitted` — the window a finger could place in. */
  canPlace: boolean
  dispatch: (action: BirdieChipAction) => ChipOutcome
  onSkip: (skip: AutoBetSkip, plan: AutoBetPlan) => void
  /**
   * A round the ceiling trimmed: it PLACED, but on fewer options than the zone
   * caught. Separate from `onSkip` because nothing was refused — the player
   * simply has to be told the rule did less than it wanted, or the feature is
   * quietly deciding on its own.
   */
  onCapped: (placed: number, caught: number) => void
  onStopped: (reason: AutoBetStopReason) => void
  playerId: string | undefined
}): UseBirdieAutoBet {
  const { raceId, tiles, limits, marketIdByOption, canPlace } = input
  const [state, setState] = useState<AutoBetMachineState>(IDLE_AUTO_BET)

  // Latest-value refs (the board's B3 pattern): the step reads the board's
  // dispatch and the two notice callbacks at the moment it runs, and the
  // planning effect is keyed on the round's inputs alone.
  const stateRef = useRef(state)
  stateRef.current = state
  const inputRef = useRef(input)
  inputRef.current = input

  const run = useCallback((event: AutoBetEvent): void => {
    const { dispatch, onSkip, onCapped, onStopped, playerId } = inputRef.current
    const ruleBefore = stateRef.current.rule
    const rule = event.type === 'arm' ? event.rule : ruleBefore

    const log = (type: AutoBetLogType, reason?: string): void => {
      if (rule === null) return
      datadogLogs.logger.info('auto_bet', {
        user_action: {
          type,
          game: 'birdie',
          playerId,
          zone: { min: rule.zone.min, max: rule.zone.max },
          markets: rule.markets,
          ...(reason === undefined ? {} : { reason })
        }
      })
    }
    const commit = (next: AutoBetMachineState): void => {
      if (next === stateRef.current) return
      stateRef.current = next
      setState(next)
    }

    const step = autoBetStep(stateRef.current, event)
    commit(step.state)
    // The board is the rule's while it is armed, so a round it answers starts
    // empty whatever the answer — otherwise chips the board carried forward
    // from a held submission would ride out on a round the pill says was
    // skipped. `applyAutoBetPlan` clears again before it places; on an empty
    // board that is a no-op.
    if (clearsBoard(step, event)) dispatch({ type: 'clear' })
    let stopped = step.stopped
    if (event.type === 'arm') log('started')

    // A plan comes back from `roundOpen` only. A skip the planner found is
    // reported as such — unless it also stopped the rule, in which case the
    // stop says why and a second toast would only repeat it. A clean plan is
    // put on the board and its outcome fed back, which can stop the rule too.
    const plan = step.plan
    if (plan !== undefined && stopped === undefined && event.type === 'roundOpen') {
      if (plan.skip !== undefined) {
        onSkip(plan.skip, plan)
        log('skipped', plan.skip)
      } else {
        const placed = applyAutoBetPlan(plan, dispatch)
        const report = autoBetStep(stateRef.current, {
          type: 'placed',
          raceId: event.raceId,
          ok: placed.ok,
          ...(placed.skip === undefined ? {} : { skip: placed.skip })
        })
        commit(report.state)
        stopped = report.stopped
        if (placed.ok) {
          log('bet_placed')
          if (plan.cappedFrom !== undefined) {
            onCapped(plan.placements.length, plan.cappedFrom)
            log('capped')
          }
        }
        else if (stopped === undefined && placed.skip !== undefined) {
          onSkip(placed.skip, plan)
          log('skipped', placed.skip)
        }
      }
    }

    if (stopped !== undefined) {
      onStopped(stopped)
      log('stopped', stopped)
    }
  }, [])

  const armed = state.rule !== null

  // The open round is answered whenever the rule is armed and a finger could
  // place: on arming mid-window, on the odds arriving, on the reset landing.
  // It runs often — the balance moves `limits` — and the machine ignores a
  // race id it has already planned, so a repeat costs nothing.
  useEffect(() => {
    if (!armed || !canPlace || raceId === undefined) return
    run({ type: 'roundOpen', raceId, tiles, limits, marketIdByOption })
  }, [armed, canPlace, limits, marketIdByOption, raceId, run, tiles])

  const arm = useCallback(
    (rule: AutoBetRule) => {
      run({ type: 'arm', rule, nowMs: Date.now() })
    },
    [run]
  )
  const disarm = useCallback(() => {
    run({ type: 'disarm' })
  }, [run])
  const noteManualEdit = useCallback(() => {
    run({ type: 'manualEdit' })
  }, [run])
  const noteWireResult = useCallback(
    (wireRaceId: string | undefined, betsActuallySent: boolean) => {
      if (wireRaceId === undefined) return
      run({ type: 'wireResult', raceId: wireRaceId, betsActuallySent })
    },
    [run]
  )

  const previewFor = (rule: AutoBetRule): AutoBetPlan | null =>
    canPlace ? planAutoBets({ tiles, rule, limits, marketIdByOption }) : null

  return { state, armed, arm, disarm, noteManualEdit, noteWireResult, previewFor }
}
